/**
 * Sliding Underline / Magic Line Component
 *
 * Usage:
 * 1. Add [data-sliding-underline] to the container
 * 2. Add [data-sliding-underline-item] to each hoverable item
 * 3. Add .is-active or .current class to the active item
 *
 * Example:
 * <nav data-sliding-underline data-sliding-underline-offset="5">
 *   <a href="#" data-sliding-underline-item class="is-active">Home</a>
 *   <a href="#" data-sliding-underline-item>About</a>
 * </nav>
 */

class SlidingUnderline {
  constructor(container) {
    this.container = container;
    this.line = null;
    this.items = [];
    this.activeItem = null;
    this.isInitialized = false;
    this._hasPositioned = false;

    this.init();
  }

  init() {
    if (this.isInitialized) return;

    // Create the line element
    this.line = document.createElement('div');
    this.line.className = 'sliding-underline__line';
    this.container.appendChild(this.line);

    // Set custom properties from data attributes
    const height = this.container.dataset.slidingUnderlineHeight;
    const color = this.container.dataset.slidingUnderlineColor;
    const offset = this.container.dataset.slidingUnderlineOffset;

    if (height) this.container.style.setProperty('--sliding-underline-height', height + 'px');
    if (color) this.container.style.setProperty('--sliding-underline-color', color);
    if (offset) this.container.style.setProperty('--sliding-underline-offset', offset + 'px');

    // Get items and bind events
    this.bindEvents();

    // Find and move to active item on init
    this.findActiveItem();
    if (this.activeItem) {
      this.moveTo(this.activeItem);
      this.container.classList.add('has-active');
      this._hasPositioned = true;
    }

    this.isInitialized = true;
  }

  findActiveItem() {
    // Look for active item with various class names
    this.activeItem = this.container.querySelector(
      '[data-sliding-underline-item].is-active, ' +
      '[data-sliding-underline-item].active, ' +
      '[data-sliding-underline-item].current, ' +
      '[data-sliding-underline-item][aria-current="page"]'
    );
  }

  bindEvents() {
    this.items = this.container.querySelectorAll('[data-sliding-underline-item]');

    this.items.forEach((item) => {
      item.addEventListener('mouseenter', () => {
        // First hover: position at first item without transition so it doesn't fly from left:0
        if (!this._hasPositioned && this.items.length > 0) {
          this.line.style.transition = 'none';
          this.moveTo(this.items[0]);
          // Force reflow then restore transition
          this.line.offsetHeight;
          this.line.style.transition = '';
          this._hasPositioned = true;
        }
        this.moveTo(item);
        this.container.classList.add('is-hovering');
      });

      // Re-detect active item after click (other handlers may have changed is-active)
      item.addEventListener('click', () => {
        requestAnimationFrame(() => {
          this.findActiveItem();
          if (this.activeItem) {
            this.moveTo(this.activeItem);
            this.container.classList.add('has-active');
          }
        });
      });
    });

    this.container.addEventListener('mouseleave', () => {
      this.container.classList.remove('is-hovering');
      // Re-find active item in case classes changed since init
      this.findActiveItem();
      if (this.activeItem) {
        this.moveTo(this.activeItem);
        this.container.classList.add('has-active');
      } else {
        this.container.classList.remove('has-active');
      }
    });
  }

  moveTo(item) {
    const containerRect = this.container.getBoundingClientRect();
    const itemRect = item.getBoundingClientRect();

    const left = itemRect.left - containerRect.left;
    const width = itemRect.width;

    this.line.style.left = left + 'px';
    this.line.style.width = width + 'px';
  }

  // Update active item (useful when active state changes via JS)
  setActive(item) {
    this.activeItem = item;
    if (item) {
      this.moveTo(item);
      this.container.classList.add('has-active');
    } else {
      this.container.classList.remove('has-active');
    }
  }

  // Refresh - re-find active item and rebind events
  refresh() {
    this.bindEvents();
    this.findActiveItem();
    if (this.activeItem) {
      this.moveTo(this.activeItem);
      this.container.classList.add('has-active');
    }
  }

  destroy() {
    if (this.line && this.line.parentNode) {
      this.line.parentNode.removeChild(this.line);
    }
    this.container.classList.remove('is-hovering', 'has-active');
    this.isInitialized = false;
  }
}

// Auto-initialize all sliding underline containers
function initSlidingUnderlines() {
  document.querySelectorAll('[data-sliding-underline]').forEach((container) => {
    if (!container._slidingUnderline) {
      container._slidingUnderline = new SlidingUnderline(container);
    }
  });
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSlidingUnderlines);
} else {
  initSlidingUnderlines();
}

// Re-initialize on Shopify section load (for theme editor)
document.addEventListener('shopify:section:load', initSlidingUnderlines);

// Export for manual use
window.SlidingUnderline = SlidingUnderline;
window.initSlidingUnderlines = initSlidingUnderlines;
